import React, { useState, Fragment, useEffect } from "react";
import List from "@mui/material/List";
import ListItemBase from "@mui/material/ListItem";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Checkbox from "@mui/material/Checkbox";
import { Box, Button, Chip, Typography } from "@mui/material";

import RpMUIDialog from "@/components/modals/RpMUIModal";
import { InfoOutlined } from "@mui/icons-material";

type CreativeOptionValue = string | number;

type CreativeOption = {
  label?: React.ReactNode;
  value: CreativeOptionValue;
  is_recommended?: boolean;
};

type BlockedSelectionInfo = {
  packageName?: string;
  channelNames: string[];
  message?: string;
};

type CreativeOptionsProps = {
  creativesOptions?: CreativeOption[];
  initiallySelected?: CreativeOptionValue[];
  currentInView?: CreativeOptionValue;
  onClickView?: (_value: CreativeOptionValue) => void;
  onSelect?: (_values: CreativeOptionValue[]) => void;
  creative_select_flag?: boolean;
  creative_select_max_value?: number;
  initialDisabled?: boolean;
  canSelectCreative?: (
    _value: CreativeOptionValue
  ) => true | string | BlockedSelectionInfo;
  selectionBlockedMessage?: string;
  isTabletOrMobile?: boolean;
};

const ListItem = ListItemBase as unknown as React.ComponentType<
  React.ComponentProps<typeof ListItemBase> & {
    button?: boolean;
  }
>;

export default function CreativeOptions({
  creativesOptions = [],
  initiallySelected = [],
  currentInView,
  onClickView = () => {},
  onSelect = () => {},
  creative_select_flag,
  creative_select_max_value,
  initialDisabled,
  canSelectCreative,
  selectionBlockedMessage = "Enable at least one channel for this package before selecting it.",
}: CreativeOptionsProps) {
  const [checked, setChecked] = useState(initiallySelected);
  const [showMaxMsg, setShowMaxMsg] = useState(false);
  const [showSelectionBlockedMsg, setShowSelectionBlockedMsg] = useState(false);
  const [blockedSelectionInfo, setBlockedSelectionInfo] =
    useState<BlockedSelectionInfo>({
      packageName: "",
      channelNames: [],
      message: selectionBlockedMessage,
    });
  const [blockedSelectionMessage, setBlockedSelectionMessage] = useState(
    selectionBlockedMessage
  );

  useEffect(() => {
    setChecked(initiallySelected);
  }, [initiallySelected]);

  const isMaxSelected = () =>
    creative_select_flag && checked?.length === creative_select_max_value;

  const getBlockedSelectionMessage = (v: CreativeOptionValue) => {
    if (!canSelectCreative) {
      return "";
    }

    const result = canSelectCreative(v);

    if (result === true) {
      return "";
    }

    return result || selectionBlockedMessage;
  };

  const handleSelectCreative = (
    v: CreativeOptionValue,
    e: React.MouseEvent<HTMLButtonElement>
  ) => {
    e.stopPropagation(); // Prevent triggering preview when clicking checkbox
    let newChecked = [...checked];
    if (creative_select_flag) {
      // multi select
      const idx = checked.indexOf(v);
      if (idx === -1) {
        if (isMaxSelected()) {
          setShowMaxMsg(true);
          return;
        }
        const blockedMessage = getBlockedSelectionMessage(v);
        if (blockedMessage) {
          if (typeof blockedMessage === "string") {
            setBlockedSelectionMessage(blockedMessage);
          } else {
            setBlockedSelectionInfo(blockedMessage);
          }
          setShowSelectionBlockedMsg(true);
          return;
        }
        newChecked.push(v);
        setChecked(newChecked);
      } else {
        newChecked.splice(idx, 1);
        setChecked(newChecked);
      }
    } else {
      // single select
      newChecked = [v];
      const idx = checked.indexOf(v);
      if (idx !== -1) {
        newChecked = [];
      } else {
        const blockedMessage = getBlockedSelectionMessage(v);
        if (blockedMessage) {
          if (typeof blockedMessage === "string") {
            setBlockedSelectionMessage(blockedMessage);
          } else {
            setBlockedSelectionInfo(blockedMessage);
          }
          setShowSelectionBlockedMsg(true);
          return;
        }
      }
      setChecked(newChecked);
    }
    onSelect(newChecked);
  };

  const handleNameClick = (v: CreativeOptionValue) => {
    onClickView(v); // Show preview when clicking the name
  };

  const isSelectedOption = (v: CreativeOptionValue) =>
    checked.length ? checked.indexOf(v) !== -1 : false;

  return (
    <div className="creative-options mr-2">
      <List>
        {creativesOptions.map((c) => {
          const isSelected = isSelectedOption(c.value);
          const isPreview = currentInView === c.value;

          return (
            <Fragment key={c.value}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 8,
                  backgroundColor: isSelected
                    ? "var(--primary-tint)"
                    : "transparent",
                  borderRadius: "var(--radius-container)",
                  padding: "8px",
                  marginRight: 5,
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: "36px",
                    borderRadius: "var(--radius-control)",
                  }}
                >
                  <Checkbox
                    edge="start"
                    checked={isSelected}
                    tabIndex={-1}
                    disableRipple
                    onClick={(e) => {
                      if (initialDisabled) {
                        return;
                      }
                      handleSelectCreative(c.value, e);
                    }}
                    inputProps={{
                      "aria-labelledby": `checkbox-list-label-${c.value}`,
                    }}
                    disabled={initialDisabled}
                    sx={{
                      color: "var(--primary-base)",
                      "&.Mui-checked": {
                        color: "var(--primary-base)",
                      },
                    }}
                  />
                </ListItemIcon>
                <ListItem
                  dense
                  button
                  onClick={() => {
                    handleNameClick(c.value);
                  }}
                  sx={{
                    backgroundColor: isSelected
                      ? "var(--primary-base)"
                      : isPreview && !isSelected
                        ? "var(--primary-tint)"
                        : "var(--neutral-white)",
                    color: isSelected
                      ? "var(--neutral-white)"
                      : isPreview && !isSelected
                        ? "var(--palette-text-primary)"
                        : "var(--primary-base)",
                    border:
                      (isPreview && !isSelected) || isSelected
                        ? "1px solid var(--primary-base)"
                        : "1px solid var(--neutral-200)",
                    borderRadius: "var(--radius-control)",
                    paddingRight: 1,
                    marginRight: 1,
                    flex: 1,
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: isSelected
                        ? "var(--primary-base)"
                        : isPreview && !isSelected
                          ? "var(--primary-tint)"
                          : "var(--neutral-white)",
                      color: isSelected
                        ? "var(--neutral-white)"
                        : isPreview && !isSelected
                          ? "var(--palette-text-primary)"
                          : "var(--primary-base)",
                    },
                  }}
                >
                  <ListItemText
                    id={`checkbox-list-label-${c.value}`}
                    primary={
                      <Box
                        component="span"
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          alignItems: "flex-start",
                          gap: "6px",
                        }}
                      >
                        <Box component="span">{c.label}</Box>
                        {c.is_recommended ? (
                          <Chip
                            data-testid="recommended-package-tag"
                            label="Recommended"
                            sx={{
                              backgroundColor: "var(--status-warning-tint)",
                              color: "var(--status-warning)",
                              borderRadius: "var(--radius-round)",
                              height: 22,
                              letterSpacing: 0,
                              "& .MuiChip-label": {
                                px: "8px",
                                fontSize: "12px",
                                lineHeight: "16px",
                                fontWeight: 800,
                              },
                            }}
                          />
                        ) : null}
                      </Box>
                    }
                    className="creative-option-label"
                    slotProps={{
                      primary: {
                        sx: {
                          whiteSpace: "normal",
                          overflowWrap: "anywhere",
                        },
                      },
                    }}
                    sx={{
                      fontWeight: isPreview ? "bolder" : "normal",
                      textDecoration: "none",
                    }}
                  />
                </ListItem>
              </div>
            </Fragment>
          );
        })}
      </List>

      <RpMUIDialog
        open={showMaxMsg}
        onClose={() => setShowMaxMsg(false)}
        maxWidth="xs"
        persists
      >
        <div className="px-3">
          <div className="text-center mb-2">
            <InfoOutlined color="error" fontSize="large" />
          </div>
          <Typography>
            You've selected the max amount of creatives, to select another
            please unselect one of your currently selected creatives.
          </Typography>
          <div className="text-center mt-3">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowMaxMsg(false)}
            >
              OK
            </Button>
          </div>
        </div>
      </RpMUIDialog>
      <RpMUIDialog
        open={showSelectionBlockedMsg}
        onClose={() => setShowSelectionBlockedMsg(false)}
        maxWidth="xs"
        persists
      >
        <div className="px-3">
          <div className="text-center mb-2">
            <InfoOutlined color="error" fontSize="large" />
          </div>
          {blockedSelectionInfo.packageName ? (
            <Typography>
              <Box component="span" className="fw-b">
                {blockedSelectionInfo.packageName}
              </Box>{" "}
              cannot be selected because all of its channels are disabled.
              Enable one of these channels first:{" "}
              <Box component="span" className="fw-b">
                {blockedSelectionInfo.channelNames.join(", ")}
              </Box>
              .
            </Typography>
          ) : (
            <Typography>{blockedSelectionMessage}</Typography>
          )}
          <div className="text-center mt-3">
            <Button
              variant="contained"
              color="primary"
              onClick={() => setShowSelectionBlockedMsg(false)}
            >
              OK
            </Button>
          </div>
        </div>
      </RpMUIDialog>
    </div>
  );
}
